import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../redux/store';
import {
  sendEmailVerificationCode,
  confirmEmailVerificationCode,
  fetchUserData,
} from '../../redux/authSlice';
import { useNavigate } from 'react-router-dom';
import CircularProgress from '@mui/material/CircularProgress';

const VerifyEmail: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const user = useSelector((state: RootState) => state.auth.user);

  // ✅ use status instead of non-existent loading
  const status = useSelector((state: RootState) => state.auth.status);
  const loading = status === 'loading';

  const [code, setCode] = useState('');
  const [message, setMessage] = useState('');
  const [sent, setSent] = useState(false);

  useEffect(() => {
    if (
      user?.userId &&
      !user.verifyEmail &&
      user.firstName == null // only fetch if profile isn’t loaded yet
    ) {
      dispatch(fetchUserData(user.uid));
    }
  }, [dispatch, user?.userId, user?.verifyEmail, user?.firstName]);

  const sendCode = async () => {
    if (!user?.email) {
      setMessage('⚠️ You must be logged in.');
      return;
    }

    const result = await dispatch(sendEmailVerificationCode({ email: user.email }));
    if (sendEmailVerificationCode.fulfilled.match(result)) {
      setSent(true);
      setMessage('📩 Verification code sent to your email.');
    } else {
      setMessage(result.payload as string);
    }
  };

  const verifyCode = async () => {
    const result = await dispatch(confirmEmailVerificationCode({ inputCode: code }));
    if (confirmEmailVerificationCode.fulfilled.match(result)) {
      setMessage('✅ Email verified successfully!');
      setTimeout(() => navigate(-1), 1500);
    } else {
      setMessage(result.payload as string);
    }
  };

  return (
    <div style={styles.container}>
      <h2 style={styles.title}>Verify Your Email</h2>

      {sent ? (
        <>
          <input
            placeholder="Enter 6-digit code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            style={styles.input}
          />
          <div style={styles.gradientWrapper}>
            <button onClick={verifyCode} style={styles.button} disabled={loading}>
              {loading ? <CircularProgress size={20} style={{ color: '#fff' }} /> : 'Verify Code'}
            </button>
          </div>
        </>
      ) : (
        <div style={styles.gradientWrapper}>
          <button onClick={sendCode} style={styles.button} disabled={loading}>
            {loading ? <CircularProgress size={20} style={{ color: '#fff' }} /> : 'Send Verification Code'}
          </button>
        </div>
      )}

      {message && <p style={styles.message}>{message}</p>}
    </div>
  );
};

export default VerifyEmail;

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    background: '#fff',
    color: '#000',
    minHeight: '100vh',
    padding: '50px 24px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  title: {
    fontSize: 22,
    fontWeight: 'bold',
    marginBottom: 20,
  },
  input: {
    backgroundColor: '#f9f9f9',
    borderRadius: 10,
    border: '1px solid #ccc',
    color: '#000',
    padding: '14px',
    marginBottom: 12,
    width: '100%',
    maxWidth: 400,
    fontSize: 16,
  },
  gradientWrapper: {
    background: 'linear-gradient(180deg, #D7262C 0%, #741F4C 50%, #0A1C3F 100%)',
    borderRadius: 30,
    padding: '2px',
    width: '100%',
    maxWidth: 400,
    marginTop: 8,
  },
  button: {
    backgroundColor: '#fff',
    color: '#000',
    fontWeight: 600,
    fontSize: 16,
    borderRadius: 30,
    border: 'none',
    padding: '12px 24px',
    cursor: 'pointer',
    width: '100%',
    transition: 'all 0.3s ease',
  },
  message: {
    marginTop: 24,
    fontSize: 15,
    fontWeight: 500,
    color: '#741F4C',
    textAlign: 'center',
  },
};
